/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import {
    Box, Container, Grid, GridItem, Flex, Text, Heading, Button,
    HStack, VStack, Badge, Collapse, Divider, Spinner, Tooltip, Link as ChakraLink,
    useDisclosure,
} from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
     selectBrandAction, selectVendorAction,
} from 'redux/actions/productActions';
import { MdVerified } from 'react-icons/md';
import {
    FaWhatsapp, FaHeart, FaChevronDown, FaTag,
} from 'react-icons/fa';
import { BsBox, BsBoxSeam } from 'react-icons/bs';
import {
    addCommasToNumber, calculateProfit, getDeliveryTimeline,
} from 'utils/utils';
import { useCurrency, useNairaToUSDToCurrency } from 'hooks/useCurrency';
import useCountdown from 'hooks/useCountDown';
import {
    useGetProductById, useGetVariantById, useGetItemGroup,
} from 'hooks/useProducts';
import { useVendorDetails } from 'hooks/useCustomers';
import { useVendorByName } from 'hooks/useVendors';
import { ProductDetailsSection } from './components/DashboardStyles';
import Loader from 'components/loader';
import SimilarProducts from './components/SimilarProducts';
import AddToCartModal from './components/AddToCartModal';
import ImportModal from 'components/Dashboard/ImportModal';
import ProductSampleRequestModal from './crm/ProductSampleModal';

/* Extracted components & utilities */
import ProductImageGallery from './ProductDetail/ProductImageGallery';
import VariantsPanel       from './ProductDetail/VariantsPanel';
import LimitedOfferAlert   from './ProductDetail/LimitedOfferAlert';
import useLogisticsRate    from './ProductDetail/useLogisticsRate';
import {
    parseProductImages, resolveVariantImage, buildWhatsAppUrl,
} from './ProductDetail/productUtils';

import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
countries.registerLocale(en);

/* ─────────────────────────────────────────────────────────────
   HELPER — safe access to custom fields
   ───────────────────────────────────────────────────────────── */
const getCfDirect = (item, key) => {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
    if (item?.custom_field_hash?.[key] !== undefined) return item.custom_field_hash[key];
    return null;
};

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';
const WARNING     = '#EA580C';
const WARNING_BG  = '#FFF7ED';
const INFO        = '#2563EB';
const INFO_BG     = '#EFF6FF';
const FONT        = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   SUPPORT CONFIG — externalized from hardcoded values
   ───────────────────────────────────────────────────────────── */
const SUPPORT_WHATSAPP =
    (typeof process !== 'undefined' && process.env?.REACT_APP_SUPPORT_WHATSAPP) ||
    '2348000000000';

/* ─────────────────────────────────────────────────────────────
   VENDOR MOQ PANEL
   ───────────────────────────────────────────────────────────── */
const VendorMoqPanel = ({ vendorId: vendorIdProp, vendorName }) => {
    const { vendor: resolvedVendor, isLoading: lookupLoading } = useVendorByName(
        vendorIdProp ? null : vendorName
    );
    const resolvedVendorId = vendorIdProp ?? resolvedVendor?.contact_id ?? null;
    const { vendorMoq, isLoading: moqLoading } = useVendorDetails(resolvedVendorId);
    const isLoading = lookupLoading || moqLoading;
    const moqConverted = useCurrency(vendorMoq ?? 0);
    const sym = moqConverted?.symbol ?? '₦';
    const fmt = (v) =>
        Number(v ?? 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const fmtShort = (v) => {
        const n = Number(v ?? 0);
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 10_000)    return `${Math.round(n / 1000)}K`;
        return fmt(n);
    };

    const priceValue = moqConverted?.price ?? 0;
    const useShortFormat = priceValue >= 10_000;

    if (!resolvedVendorId && !lookupLoading) return null;

    return (
        <Box
            bg={SURFACE}
            borderRadius="12px"
            border="1px solid"
            borderColor={BORDER}
            boxShadow="0 1px 3px rgba(27,59,95,0.06)"
            overflow="hidden"
            fontFamily={FONT}
            w="full"
            minW="0"
        >
            <Box h="3px" bg={vendorMoq ? NAVY : BORDER} />
            <Box p={{ base: '10px 12px', md: '12px 14px' }}>
                <Flex justify="space-between" align="flex-start" gap="10px" w="full" minW="0">
                    <Flex align="center" gap="10px" minW="0" flex="1">
                        <Box p="6px" bg={CYAN} borderRadius="6px" color={NAVY} flexShrink={0}>
                            <BsBoxSeam size={14} />
                        </Box>
                        <Box minW="0" flex="1">
                            <Text fontSize="12px" fontWeight="700" color={INK} lineHeight="1.3" noOfLines={1}>
                                Vendor minimum order
                            </Text>
                            {vendorName && (
                                <Text fontSize="10.5px" color={FAINT} noOfLines={1} mt="1px">
                                    {vendorName}
                                </Text>
                            )}
                        </Box>
                    </Flex>
                    {isLoading ? (
                        <Spinner size="sm" color={NAVY} flexShrink={0} />
                    ) : vendorMoq ? (
                        <Box textAlign="right" flexShrink={0} minW="0">
                            <Text
                                fontSize={{ base: '14px', md: '15px' }}
                                fontWeight="800"
                                color={NAVY}
                                fontFamily={FONT}
                                lineHeight="1.2"
                                whiteSpace="nowrap"
                                title={`${sym} ${fmt(priceValue)}`}
                            >
                                {sym} {useShortFormat ? fmtShort(priceValue) : fmt(priceValue)}
                            </Text>
                            <Text fontSize="9.5px" color={FAINT} mt="2px">
                                minimum order
                            </Text>
                        </Box>
                    ) : (
                        <Text fontSize="11px" color={FAINT} flexShrink={0}>
                            Not specified
                        </Text>
                    )}
                </Flex>

                {!isLoading && vendorMoq && (
                    <Box
                        mt="10px"
                        p="10px 12px"
                        bg={SURFACE_ALT}
                        borderRadius="8px"
                        border="1px solid"
                        borderColor={BORDER}
                    >
                        <Flex align="center" gap="6px" mb="10px">
                            <Box color={SUCCESS} display="flex">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Box>
                            <Text fontSize="10px" fontWeight="800" color={INK} letterSpacing="0.04em">
                                OBANA BUYER PROTECTION
                            </Text>
                        </Flex>
                        <Box
                            display="grid"
                            gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                            gap={{ base: '6px', md: '8px 16px' }}
                        >
                            <Flex align="flex-start" gap="8px" minW="0">
                                <Box w="4px" h="4px" borderRadius="full" bg={SUCCESS} mt="6px" flexShrink={0} />
                                <Text fontSize="10.5px" color={MUTED} lineHeight="1.45">
                                    <Text as="span" color={INK} fontWeight="700">Payment held</Text> until delivery is confirmed
                                </Text>
                            </Flex>
                            <Flex align="flex-start" gap="8px" minW="0">
                                <Box w="4px" h="4px" borderRadius="full" bg={SUCCESS} mt="6px" flexShrink={0} />
                                <Text fontSize="10.5px" color={MUTED} lineHeight="1.45">
                                    <Text as="span" color={INK} fontWeight="700">Verified vendors</Text> — every supplier vetted
                                </Text>
                            </Flex>
                            <Flex align="flex-start" gap="8px" minW="0">
                                <Box w="4px" h="4px" borderRadius="full" bg={SUCCESS} mt="6px" flexShrink={0} />
                                <Text fontSize="10.5px" color={MUTED} lineHeight="1.45">
                                    <Text as="span" color={INK} fontWeight="700">Full refund</Text> if items don't match description
                                </Text>
                            </Flex>
                            <Flex align="flex-start" gap="8px" minW="0">
                                <Box w="4px" h="4px" borderRadius="full" bg={SUCCESS} mt="6px" flexShrink={0} />
                                <Text fontSize="10.5px" color={MUTED} lineHeight="1.45">
                                    <Text as="span" color={INK} fontWeight="700">Secure checkout</Text> with multi-currency support
                                </Text>
                            </Flex>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   DISCOUNT PANEL
   ───────────────────────────────────────────────────────────── */
const DiscountPanel = ({ product, itemRateConverted, convertedOldRate }) => {
    if (!product?.has_discount) return null;
    const discountPct = Number(getCfDirect(product, 'cf_discount_percentage') || 0);
    const discountedPrice = Number(itemRateConverted?.price || 0);
    const originalPrice = Number(convertedOldRate?.price || 0);
    const savings = originalPrice > 0 ? (originalPrice - discountedPrice).toFixed(2) : null;

    return (
        <Box
            bg={DANGER_BG}
            border="1px solid"
            borderColor={`${DANGER}30`}
            borderRadius="10px"
            p="12px 14px"
            mb="14px"
            fontFamily={FONT}
        >
            <Flex align="baseline" gap="10px" flexWrap="wrap" mb="6px">
                <Text fontSize={{ base: '22px', md: '26px' }} fontWeight="800" color={DANGER} lineHeight="1">
                    {itemRateConverted?.symbol} {addCommasToNumber(discountedPrice.toFixed(2))}
                </Text>
                {originalPrice > 0 && (
                    <Text
                        fontSize={{ base: '13px', md: '14px' }}
                        fontWeight="500"
                        color={FAINT}
                        textDecoration="line-through"
                    >
                        {convertedOldRate?.symbol} {addCommasToNumber(originalPrice.toFixed(2))}
                    </Text>
                )}
                <Badge
                    bg={DANGER}
                    color="white"
                    fontSize="10px"
                    fontWeight="800"
                    px="8px"
                    py="3px"
                    borderRadius="6px"
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    letterSpacing="0.04em"
                >
                    <FaTag size={9} />-{discountPct}% OFF
                </Badge>
            </Flex>
            {savings && Number(savings) > 0 && (
                <Text fontSize="11px" color={SUCCESS} fontWeight="700">
                    You save {itemRateConverted?.symbol} {addCommasToNumber(savings)} per unit
                </Text>
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   SPECIFICATIONS PANEL
   ───────────────────────────────────────────────────────────── */
const SpecificationsPanel = ({ items }) => {
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });

    return (
        <Box
            bg={SURFACE}
            borderRadius="12px"
            border="1px solid"
            borderColor={BORDER}
            boxShadow="0 1px 3px rgba(27,59,95,0.06)"
            overflow="hidden"
            fontFamily={FONT}
        >
            <Flex
                p="16px 18px"
                justify="space-between"
                align="center"
                cursor="pointer"
                onClick={onToggle}
                _hover={{ bg: SURFACE_ALT }}
                transition="background 0.2s ease"
                as="button"
                type="button"
                w="full"
                bg="transparent"
                border="none"
                textAlign="left"
                aria-expanded={isOpen}
            >
                <Text fontSize="14px" fontWeight="800" color={INK} fontFamily={FONT}>
                    Product specifications
                </Text>
                <Box
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.3s ease"
                    color={FAINT}
                >
                    <FaChevronDown size={14} />
                </Box>
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box px="18px" pb="14px">
                    <VStack align="stretch" spacing="0">
                        {items.map((item, idx) => (
                            <Flex
                                key={`${item.label}-${idx}`}
                                justify="space-between"
                                align="center"
                                py="10px"
                                gap="14px"
                                borderBottom={idx < items.length - 1 ? `1px solid ${BORDER}` : 'none'}
                            >
                                <Text fontSize="11px" fontWeight="700" color={MUTED} flex="1">
                                    {item.label}
                                </Text>
                                <Box fontSize="11px" color={INK} flex="1" textAlign="right" fontWeight="600">
                                    {item.value}
                                </Box>
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            </Collapse>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function SingleProductDetail() {
    const params = useParams();
    const dispatch = useDispatch();
    const brands = useSelector((state) => state.productReducer?.brands);
    const userLocationReducer = useSelector((state) => state.userLocationReducer);

    const [imageInView, setImageInView] = useState(null);
    const [manualImage, setManualImage] = useState(null);
    // const [quantity, setQuantity] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState({});

    const cartModal   = useDisclosure();
    const importModal = useDisclosure();
    const sampleModal = useDisclosure();

    const { data: baseProduct, isLoading: baseLoading } = useGetProductById(params?.id);
    const groupId = baseProduct?.group_id ?? null;
    const { data: itemGroup, isLoading: groupLoading } = useGetItemGroup(groupId);

    useEffect(() => {
        if (!itemGroup || !baseProduct) return;
        const init = {};
        if (baseProduct.attribute_id1 && baseProduct.attribute_option_id1)
            init[baseProduct.attribute_id1] = baseProduct.attribute_option_id1;
        if (baseProduct.attribute_id2 && baseProduct.attribute_option_id2)
            init[baseProduct.attribute_id2] = baseProduct.attribute_option_id2;
        if (Object.keys(init).length > 0) setSelectedOptions(init);
    }, [itemGroup, baseProduct]);

    useEffect(() => {
        window.scrollTo(0, 0);
        setManualImage(null);
        setImageInView(null);
        setSelectedOptions({});
    }, [params?.id]);

    const selectedVariantStub = useMemo(() => {
        if (!itemGroup?.items?.length || Object.keys(selectedOptions).length === 0) return null;
        return (
            itemGroup.items.find((item) => {
                const match1 =
                    !itemGroup.attribute_id1 ||
                    item.attribute_option_id1 === selectedOptions[itemGroup.attribute_id1];
                const match2 =
                    !itemGroup.attribute_id2 ||
                    item.attribute_option_id2 === selectedOptions[itemGroup.attribute_id2];
                return match1 && match2;
            }) ?? null
        );
    }, [itemGroup, selectedOptions]);

    const variantIdToFetch =
        selectedVariantStub?.item_id && selectedVariantStub.item_id !== params?.id
            ? selectedVariantStub.item_id
            : null;

    const { data: variantFull } = useGetVariantById(variantIdToFetch);
    // const effectiveProduct = useMemo(
    //     () => variantFull ?? baseProduct,
    //     [variantFull, baseProduct]
    // );
    const effectiveProduct = useMemo(() => {
    const base = variantFull ?? baseProduct;
    if (!base) return base;

    /* Normalize cf_* fields. Backend inconsistently returns them either at
       top level or under custom_field_hash. If hash is empty/missing,
       populate it from any top-level cf_* keys so downstream reads work
       regardless of which shape arrived. */
    const hash = base.custom_field_hash;
    const hashIsEmpty = !hash || Object.keys(hash).length === 0;

    if (hashIsEmpty) {
        const cfFields = {};
        Object.keys(base).forEach((key) => {
            if (key.startsWith('cf_') && base[key] !== undefined && base[key] !== null) {
                cfFields[key] = base[key];
            }
        });
        if (Object.keys(cfFields).length > 0) {
            return { ...base, custom_field_hash: cfFields };
        }
    }

    return base;
}, [variantFull, baseProduct]);

    const vendorId =
        effectiveProduct?.vendor_id ??
        effectiveProduct?.custom_field_hash?.cf_vendor_id ??
        null;
    const vendorName =
        effectiveProduct?.vendor_name ??
        effectiveProduct?.custom_field_hash?.cf_vendor_name ??
        null;

    const userDeliveryAddress = useMemo(
        () => ({ country: userLocationReducer?.location?.country || 'Nigeria' }),
        [userLocationReducer?.location?.country]
    );

    const {
        data: logisticsInfo,
        loading: logisticsLoading,
    } = useLogisticsRate(effectiveProduct, userDeliveryAddress);

    // const productImages = useMemo(
    //     () => parseProductImages(effectiveProduct?.custom_field_hash?.cf_productimages),
    //     [effectiveProduct]
    // );
    const productImages = useMemo(() => {
    const ep = effectiveProduct;
    if (!ep) return [];

    /* Try the primary source first: parsed cf_productimages array */
    const parsed = parseProductImages(ep?.custom_field_hash?.cf_productimages);
    if (parsed.length > 0) return parsed;

    /* Fallback chain — backend's detail endpoint sometimes only populates
       one of these alternative image fields */
    const fallbacks = [
        ep?.image,
        ep?.imageurl,
        ep?.cf_singleimage,
        ep?.custom_field_hash?.cf_singleimage,
        ep?.custom_field_hash?.cf_sample_image,
    ];

    const found = fallbacks.find((v) => typeof v === 'string' && v.trim().length > 0);
    if (found) return [found];

    /* Try product_images array (Zoho sometimes uses this) */
    if (Array.isArray(ep?.product_images) && ep.product_images.length > 0) {
        return ep.product_images
            .map((p) => p?.image_url || p?.url || p)
            .filter((v) => typeof v === 'string' && v.trim().length > 0);
    }

    return [];
}, [effectiveProduct]);
    const displayedImage = useMemo(() => {
        if (manualImage) return manualImage;
        return (
            resolveVariantImage(variantFull ?? baseProduct, productImages) ||
            productImages[0] ||
            null
        );
    }, [manualImage, variantFull, baseProduct, productImages]);

    useEffect(() => {
        setImageInView(displayedImage);
    }, [displayedImage]);

    const itemRateConverted = useCurrency(effectiveProduct?.rate || 0);
    const convertedOldRate = useCurrency(effectiveProduct?.old_rate || 0);
    const convertedItemRate = useCurrency(
        effectiveProduct?.custom_field_hash?.cf_item_price_unformatted ?? 0
    );
    const convertedSuggestedRate = useCurrency(
        effectiveProduct?.custom_field_hash?.cf_retail_suggested_price > 0
            ? effectiveProduct.custom_field_hash.cf_retail_suggested_price
            : (effectiveProduct?.custom_field_hash?.cf_item_price_unformatted ?? 0) * 3
    );
    const logisticsRateConverted = useNairaToUSDToCurrency(logisticsInfo?.match?.price ?? 0);

    const countdown = useCountdown(effectiveProduct?.custom_field_hash?.cf_disable_date || '');
    const deliveryTimeline = getDeliveryTimeline(
        effectiveProduct?.custom_field_hash?.cf_vendor_location,
        userLocationReducer?.country
    );
    const profit = calculateProfit(
        effectiveProduct,
        itemRateConverted?.symbol,
        itemRateConverted?.price
    );
    const moreImagesLink = effectiveProduct?.custom_field_hash?.cf_more_image;
    const hasDiscount = !!effectiveProduct?.has_discount;
    const hasDisableDate =
        !!effectiveProduct?.custom_field_hash?.cf_disable_date && !countdown.isPast;
    const isGrouped = !!groupId;
    const isVerified = effectiveProduct?.custom_field_hash?.cf_verified === 'Yes';
    const sampleAvailable = effectiveProduct?.custom_field_hash?.cf_sample_available === 'Yes';

    const handleWhatsAppEnquiry = () => {
        window.open(
            buildWhatsAppUrl({
                phone: SUPPORT_WHATSAPP,
                productName: effectiveProduct?.name,
                productUrl: window.location.href,
            }),
            '_blank',
            'noopener,noreferrer'
        );
    };

    const specs = useMemo(() => {
        const ep = effectiveProduct;
        if (!ep) return [];

        const items = [
            ep.brand && ep.custom_field_hash?.cf_brand_id && {
                label: 'Brand',
                value: (
                    <Link
                        to={`/dashboard/brands/${ep.custom_field_hash.cf_brand_id}`}
                        onClick={() =>
                            dispatch(
                                selectBrandAction(
                                    brands?.find(
                                        (b) => b.brand_id === ep.custom_field_hash.cf_brand_id
                                    )
                                )
                            )
                        }
                        style={{ color: NAVY, textDecoration: 'underline', fontWeight: 700 }}
                    >
                        {ep.brand}
                    </Link>
                ),
            },
            {
                label: ep.unit === 'KG' ? 'Price per KG' : 'Price per unit',
                value: `${convertedItemRate?.symbol} ${addCommasToNumber(convertedItemRate?.price)}`,
            },
            ep.custom_field_hash?.cf_retail_suggested_price && {
                label: 'Recommended retail price',
                value: `${convertedSuggestedRate?.symbol} ${addCommasToNumber(convertedSuggestedRate?.price)}`,
            },
            ep.custom_field_hash?.cf_market_for && {
                label: 'Market made for',
                value: ep.custom_field_hash.cf_market_for,
            },
            ep.vendor_name && {
                label: 'Vendor',
                value: (
                    <HStack spacing="4px" justify="flex-end">
                        <Link
                            to={`/dashboard/vendors/${encodeURIComponent(ep.vendor_name)}`}
                            onClick={() =>
                                dispatch(
                                    selectVendorAction({
                                        id: ep.custom_field_hash?.cf_vendor_id ?? ep.vendor_name,
                                        name: ep.vendor_name,
                                        image: ep.custom_field_hash?.cf_vendor_image,
                                    })
                                )
                            }
                            style={{ color: NAVY, textDecoration: 'underline', fontWeight: 700 }}
                        >
                            {ep.vendor_name}
                        </Link>
                        {isVerified && (
                            <Badge
                                bg={`${INFO}15`}
                                color={INFO}
                                fontSize="9px"
                                fontWeight="800"
                                px="5px"
                                py="1px"
                                borderRadius="4px"
                                display="flex"
                                alignItems="center"
                                gap="3px"
                            >
                                <MdVerified size={9} /> Verified
                            </Badge>
                        )}
                    </HStack>
                ),
            },
            ep.custom_field_hash?.cf_sizes_run && {
                label: 'Sizes run',
                value: ep.custom_field_hash.cf_sizes_run,
            },
            ep.custom_field_hash?.cf_fabric_type && {
                label: 'Fabric type',
                value: ep.custom_field_hash.cf_fabric_type,
            },
            ep.custom_field_hash?.cf_vendor_location && {
                label: 'Country of vendor',
                value: ep.custom_field_hash.cf_vendor_location,
            },
            ep.custom_field_hash?.cf_color && {
                label: 'Available colour',
                value: ep.custom_field_hash.cf_color,
            },
            ep.unit === 'KG'
                ? ep.package_details?.weight && {
                      label: 'MOQ (KG)',
                      value: ep.package_details.weight,
                  }
                : ep.custom_field_hash?.cf_stock_available_box && {
                      label: `Stock available${ep.unit ? ` (${ep.unit})` : ''}`,
                      value: ep.custom_field_hash.cf_stock_available_box,
                  },
            {
                label: 'Estimated shipping',
                value: logisticsLoading ? (
                    <Spinner size="xs" color={NAVY} />
                ) : logisticsInfo?.match?.price ? (
                    `${logisticsRateConverted?.symbol ?? '₦'} ${addCommasToNumber(logisticsRateConverted?.price)}`
                ) : (
                    <Text as="span" color={FAINT}>Quote unavailable</Text>
                ),
            },
            {
                label: 'Shipping timeline',
                value: logisticsLoading ? (
                    <Spinner size="xs" color={NAVY} />
                ) : (
                    logisticsInfo?.match?.estimated_delivery ?? <Text as="span" color={FAINT}>Quote unavailable</Text>
                ),
            },
        ];

        return items.filter(Boolean);
    }, [
        effectiveProduct,
        brands,
        dispatch,
        convertedItemRate,
        convertedSuggestedRate,
        isVerified,
        logisticsLoading,
        logisticsInfo,
        logisticsRateConverted,
    ]);

    if (baseLoading) {
        return (
            <Flex justify="center" mt="80px">
                <Loader fullscreen={false} />
            </Flex>
        );
    }
    

    const description = isGrouped
        ? itemGroup?.description ||
          effectiveProduct?.description ||
          getCfDirect(effectiveProduct, 'cf_product_description') ||
          'No details provided'
        : effectiveProduct?.description ||
          getCfDirect(effectiveProduct, 'cf_product_description') ||
          'No details provided';

    return (
        <ProductDetailsSection>
            <Box bg={SURFACE_ALT} minH="100vh" fontFamily={FONT} overflowX="hidden">
                <Container maxW="1400px" px={{ base: '12px', md: '20px', xl: '24px' }} py="24px">

                    {hasDisableDate && (
                        <LimitedOfferAlert
                            countdown={countdown}
                            disableDate={effectiveProduct.custom_field_hash.cf_disable_date}
                        />
                    )}

                    <Grid
                        templateColumns="repeat(12, 1fr)"
                        gap={{ base: '16px', lg: '20px', xl: '28px' }}
                        w="full"
                    >
                        {/* LEFT COLUMN */}
                        <GridItem colSpan={{ base: 12, lg: 7 }} minW="0">
                            <VStack align="stretch" spacing="14px">
                                <ProductImageGallery
                                    mainImage={imageInView}
                                    images={productImages}
                                    discountPercentage={
                                        hasDiscount
                                            ? Number(effectiveProduct.cf_discount_percentage || 0)
                                            : 0
                                    }
                                    isVerified={isVerified}
                                    moreImagesLink={moreImagesLink}
                                    onSelectImage={(img) => {
                                        setImageInView(img);
                                        setManualImage(img);
                                    }}
                                />
                                
                                

                                {(() => {
                                    const desc = isGrouped
                                        ? itemGroup?.description || effectiveProduct?.description
                                        : effectiveProduct?.description;
                                    if (!desc || !String(desc).trim()) return null;
                                    return (
                                        <Box
                                            bg={SURFACE}
                                            borderRadius="12px"
                                            p="18px"
                                            border="1px solid"
                                            borderColor={BORDER}
                                            boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                                            display={{ base: 'none', lg: 'block' }}
                                        >
                                            <Heading
                                                as="h2"
                                                fontSize="14px"
                                                fontWeight="800"
                                                color={INK}
                                                fontFamily={FONT}
                                                mb="8px"
                                            >
                                                Description
                                            </Heading>
                                            <Text fontSize="12px" color={MUTED} lineHeight="1.6">
                                                {desc}
                                            </Text>
                                        </Box>
                                    );
                                })()}
                            </VStack>
                        </GridItem>

                        {/* RIGHT COLUMN */}
                        <GridItem colSpan={{ base: 12, lg: 5 }} minW="0">
                            <VStack align="stretch" spacing="10px">
                                <Box
                                    bg={SURFACE}
                                    borderRadius="12px"
                                    p={{ base: '12px 14px', md: '14px 16px' }}
                                    border="1px solid"
                                    borderColor={BORDER}
                                    boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                                >
                                    {(effectiveProduct?.brand || effectiveProduct?.mgs_brand) && (
                                        <Text
                                            color={FAINT}
                                            fontSize="10px"
                                            fontWeight="800"
                                            textTransform="uppercase"
                                            letterSpacing="0.08em"
                                            mb="6px"
                                        >
                                            {effectiveProduct.brand || effectiveProduct.mgs_brand}
                                        </Text>
                                    )}

                                    <Flex
                                        direction={{ base: 'column', md: 'row' }}
                                        justify="space-between"
                                        align={{ base: 'stretch', md: 'flex-start' }}
                                        gap={{ base: '12px', md: '20px' }}
                                        mb="12px"
                                    >
                                        <Box flex="1" minW="0">
                                            <Heading
                                                as="h1"
                                                fontSize={{ base: '20px', md: '24px' }}
                                                fontWeight="800"
                                                mb="8px"
                                                lineHeight="1.25"
                                                color={INK}
                                                fontFamily={FONT}
                                                letterSpacing="-0.02em"
                                            >
                                                {isGrouped
                                                    ? itemGroup?.group_name ?? effectiveProduct?.name
                                                    : effectiveProduct?.name}
                                            </Heading>

                                            {isGrouped && variantFull && (
                                                <Text fontSize="12px" color={MUTED} mb="10px">
                                                    Variant:{' '}
                                                    <Text as="span" color={INK} fontWeight="700">
                                                        {[
                                                            variantFull.attribute_option_name1,
                                                            variantFull.attribute_option_name2,
                                                        ].filter(Boolean).join(' / ')}
                                                    </Text>
                                                </Text>
                                            )}

                                            <HStack spacing="6px" flexWrap="wrap">
                                                {effectiveProduct?.custom_field_hash?.cf_preorder === 'Yes' ? (
                                                    <Badge
                                                        bg={WARNING_BG}
                                                        color={WARNING}
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        px="8px"
                                                        py="2px"
                                                        borderRadius="full"
                                                        border={`1px solid ${WARNING}30`}
                                                    >
                                                        ON PREORDER
                                                    </Badge>
                                                ) : (
                                                    <Badge
                                                        bg={SUCCESS_BG}
                                                        color={SUCCESS}
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        px="8px"
                                                        py="2px"
                                                        borderRadius="full"
                                                        border={`1px solid ${SUCCESS}30`}
                                                    >
                                                        IN STOCK
                                                    </Badge>
                                                )}
                                                {effectiveProduct?.custom_field_hash?.cf_clearance_sale === 'Yes' && (
                                                    <Badge
                                                        bg={DANGER_BG}
                                                        color={DANGER}
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        px="8px"
                                                        py="2px"
                                                        borderRadius="full"
                                                        border={`1px solid ${DANGER}30`}
                                                    >
                                                        CLEARANCE
                                                    </Badge>
                                                )}
                                                {isVerified && (
                                                    <Badge
                                                        bg={INFO_BG}
                                                        color={INFO}
                                                        fontSize="10px"
                                                        fontWeight="800"
                                                        px="8px"
                                                        py="2px"
                                                        borderRadius="full"
                                                        border={`1px solid ${INFO}30`}
                                                        display="flex"
                                                        alignItems="center"
                                                        gap="3px"
                                                    >
                                                        <MdVerified size={10} /> VERIFIED VENDOR
                                                    </Badge>
                                                )}
                                            </HStack>
                                        </Box>

                                        <Box textAlign={{ base: 'left', md: 'right' }} minW="0" flexShrink={0}>
                                            {hasDiscount ? (
                                                <DiscountPanel
                                                    product={effectiveProduct}
                                                    itemRateConverted={itemRateConverted}
                                                    convertedOldRate={convertedOldRate}
                                                />
                                            ) : (
                                                <Flex
                                                    alignItems="baseline"
                                                    gap="6px"
                                                    mb="6px"
                                                    justify={{ base: 'flex-start', md: 'flex-end' }}
                                                >
                                                    <Text
                                                        fontSize={{ base: '24px', md: '28px' }}
                                                        fontWeight="800"
                                                        color={NAVY}
                                                        fontFamily={FONT}
                                                        lineHeight="1"
                                                        letterSpacing="-0.02em"
                                                    >
                                                        {itemRateConverted?.symbol}{' '}
                                                        {addCommasToNumber(itemRateConverted?.price)}
                                                    </Text>
                                                    {effectiveProduct?.unit === 'KG' && (
                                                        <Text color={MUTED} fontSize="11px">
                                                            / {effectiveProduct?.package_details?.weight ?? ''}{' '}
                                                            {effectiveProduct?.package_details?.weight_unit ?? ''}
                                                        </Text>
                                                    )}
                                                </Flex>
                                            )}

                                            <Text fontSize="10.5px" color={MUTED} mb="6px">
                                                {effectiveProduct?.custom_field_hash?.cf_incl_of_shipping_and_vat === 'Yes'
                                                    ? 'Includes shipping & VAT'
                                                    : 'Excludes shipping & VAT'}
                                            </Text>

                                            {effectiveProduct?.custom_field_hash?.cf_retail_suggested_price && (
                                                <Text fontSize="12px" color={MUTED} mb="6px">
                                                    <Text as="span" fontWeight="700" color={INK}>RRP: </Text>
                                                    {effectiveProduct?.custom_field_hash?.cf_rrp_link ? (
                                                        <ChakraLink
                                                            href={effectiveProduct.custom_field_hash.cf_rrp_link}
                                                            isExternal
                                                            color={INFO}
                                                            textDecoration="underline"
                                                        >
                                                            {convertedSuggestedRate?.symbol}{' '}
                                                            {addCommasToNumber(convertedSuggestedRate?.price)}
                                                        </ChakraLink>
                                                    ) : (
                                                        <>
                                                            {convertedSuggestedRate?.symbol}{' '}
                                                            {addCommasToNumber(convertedSuggestedRate?.price)}
                                                        </>
                                                    )}
                                                </Text>
                                            )}

                                            {profit && (
                                                <Badge
                                                    bg={SUCCESS_BG}
                                                    color={SUCCESS}
                                                    fontSize="11px"
                                                    fontWeight="700"
                                                    p="6px 10px"
                                                    borderRadius="6px"
                                                    border={`1px solid ${SUCCESS}30`}
                                                    display="inline-block"
                                                >
                                                    {profit}
                                                </Badge>
                                            )}
                                        </Box>
                                    </Flex>

                                    <Divider borderColor={BORDER} my="10px" />

                                    <Flex
                                        justify="space-between"
                                        fontSize="11px"
                                        flexDir={{ base: 'column', md: 'row' }}
                                        align={{ base: 'start', md: 'center' }}
                                        gap="6px"
                                    >
                                        <HStack spacing="6px">
                                            <Text fontWeight="700" color={FAINT}>
                                                PRODUCT CODE:
                                            </Text>
                                            <Text
                                                fontWeight="700"
                                                fontFamily="monospace"
                                                color={INK}
                                                title={effectiveProduct?.sku}
                                            >
                                                {effectiveProduct?.sku}
                                            </Text>
                                        </HStack>
                                        {effectiveProduct?.custom_field_hash?.cf_product_code_vendor && (
                                            <HStack spacing="6px">
                                                <Text fontWeight="700" color={FAINT}>
                                                    VENDOR SKU:
                                                </Text>
                                                <Text
                                                    fontWeight="700"
                                                    fontFamily="monospace"
                                                    color={INK}
                                                    title={effectiveProduct.custom_field_hash.cf_product_code_vendor}
                                                >
                                                    {effectiveProduct.custom_field_hash.cf_product_code_vendor}
                                                </Text>
                                            </HStack>
                                        )}
                                    </Flex>
                                </Box>

                                {isGrouped && (
                                    <VariantsPanel
                                        itemGroup={itemGroup}
                                        groupLoading={groupLoading}
                                        setImageInView={setImageInView}
                                        setManualImage={setManualImage}
                                    />
                                )}

                                {(vendorId || vendorName) && (
                                    <VendorMoqPanel
                                        vendorId={vendorId}
                                        vendorName={vendorName}
                                    />
                                )}

                                <Box
                                    bg={SURFACE}
                                    borderRadius="12px"
                                    p="10px"
                                    border="1px solid"
                                    borderColor={BORDER}
                                    boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                                >
                                    <VStack spacing="8px" align="stretch">
                                        <HStack spacing="8px">
                                            <Button
                                                flex="1"
                                                h="36px"
                                                bg="#25D366"
                                                color="white"
                                                _hover={{ bg: '#20BA5A' }}
                                                onClick={handleWhatsAppEnquiry}
                                                leftIcon={<FaWhatsapp size={13} />}
                                                fontSize="11.5px"
                                                fontWeight="700"
                                                fontFamily={FONT}
                                                borderRadius="8px"
                                            >
                                                Make enquiry
                                            </Button>
                                            <Tooltip
                                                label={
                                                    !sampleAvailable
                                                        ? 'Vendor does not allow samples for this product'
                                                        : ''
                                                }
                                                isDisabled={sampleAvailable}
                                                hasArrow
                                                placement="top"
                                            >
                                                <Button
                                                    flex="1"
                                                    h="36px"
                                                    isDisabled={!sampleAvailable}
                                                    onClick={sampleModal.onOpen}
                                                    leftIcon={<BsBox size={13} />}
                                                    fontSize="11.5px"
                                                    fontWeight="700"
                                                    fontFamily={FONT}
                                                    borderRadius="8px"
                                                    bg={sampleAvailable ? CYAN : BORDER}
                                                    color={sampleAvailable ? NAVY : FAINT}
                                                    _hover={sampleAvailable ? { bg: '#C5EFF1' } : {}}
                                                    _disabled={{
                                                        bg: BORDER, color: FAINT, cursor: 'not-allowed',
                                                        _hover: { bg: BORDER },
                                                    }}
                                                >
                                                    {sampleAvailable ? 'Request sample' : 'No samples'}
                                                </Button>
                                            </Tooltip>
                                        </HStack>
                                        <Button
                                            w="full"
                                            h="34px"
                                            variant="outline"
                                            borderColor={BORDER}
                                            color={DANGER}
                                            onClick={importModal.onOpen}
                                            leftIcon={<FaHeart size={11} />}
                                            fontSize="11px"
                                            fontWeight="700"
                                            fontFamily={FONT}
                                            borderRadius="8px"
                                            _hover={{ bg: DANGER_BG, borderColor: DANGER }}
                                        >
                                            Add to wishlist
                                        </Button>
                                    </VStack>
                                </Box>

                                {specs.length > 0 && <SpecificationsPanel items={specs} />}

                                {(() => {
                                    const desc = isGrouped
                                        ? itemGroup?.description || effectiveProduct?.description
                                        : effectiveProduct?.description;
                                    if (!desc || !String(desc).trim()) return null;
                                    return (
                                        <Box
                                            bg={SURFACE}
                                            borderRadius="12px"
                                            p="16px"
                                            border="1px solid"
                                            borderColor={BORDER}
                                            boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                                            display={{ base: 'block', lg: 'none' }}
                                        >
                                            <Heading
                                                as="h2"
                                                fontSize="14px"
                                                fontWeight="800"
                                                color={INK}
                                                fontFamily={FONT}
                                                mb="8px"
                                            >
                                                Description
                                            </Heading>
                                            <Text fontSize="12px" color={MUTED} lineHeight="1.6">
                                                {desc}
                                            </Text>
                                        </Box>
                                    );
                                })()}
                            </VStack>
                        </GridItem>
                    </Grid>

                    <Box mt="40px" mb="24px">
                        <Heading
                            as="h2"
                            fontSize="18px"
                            fontWeight="800"
                            color={INK}
                            mb="16px"
                            fontFamily={FONT}
                            letterSpacing="-0.01em"
                        >
                            Similar products
                        </Heading>
                        <SimilarProducts productDetail={effectiveProduct} />
                    </Box>
                </Container>
            </Box>

            <AddToCartModal
                isModalOpen={cartModal.isOpen}
                closeModal={cartModal.onClose}
                setIsModalOpen={cartModal.onOpen}
                currentItem={effectiveProduct}
                quantity={{ value: 0 }}
            />
            <ImportModal
                isModalOpen={importModal.isOpen}
                closeModal={importModal.onClose}
                setIsModalOpen={importModal.onOpen}
                item={effectiveProduct}
            />
            <ProductSampleRequestModal
                isOpen={sampleModal.isOpen}
                onClose={sampleModal.onClose}
                product={effectiveProduct}
                itemGroup={itemGroup}
            />
        </ProductDetailsSection>
    );
}